import React, { useEffect, useRef, useState, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { AppContext } from "../../utils/Context/AppContext";
// import IllustrationContainer from "../../components/IllustrationContainer/IllustrationContainer";
import TypingIndicator from "../../components/TypingIndicator/TypingIndicator";
import Loader from "../../components/Loader/Loader";
import BotMessage from "../../components/BotMessage/BotMessage";
import UserMessage from "../../components/UserMessage/UserMessage";
import InputFooter from "../../components/InputFooter/InputFooter";
import { loadModelResponse } from "../../utils/helpers/modelResponsesStorage";
import user from "../../assets/user.png";
import bot from "../../assets/bot.png";
import NecaLogo from "../../assets/NecaLogoTransparent.png";

const TIPS = [
  {
    title: "Be specific.",
    body: "Include as much relevant detail and context as you can.",
  },
  {
    title: "Stay in the same chat",
    body: "when asking follow-up questions on the same topic. Niki remembers the conversation.",
  },
  {
    title: "Start a New Chat",
    body: "when changing to a different topic. This helps keep responses accurate.",
  },
];

const getGreeting = (hour) => {
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

const DataDrivenChat = () => {
  const [searchParams] = useSearchParams();
  const { accounts } = useMsal();
  const firstName = (
    accounts[0]?.name || localStorage.getItem("userName") || ""
  )
    .trim()
    .split(" ")[0];
  const [greeting] = useState(() => getGreeting(new Date().getHours()));
  const { appData, dispatch, messages, setMessages, setIsAdmin } =
    useContext(AppContext);
  // const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoader, setPageLoader] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState();
  let conversationId = searchParams.get("conversation_id");
  const chatEndRef = useRef(null);

  const handleOpenCitation = (citation, i) => {
    dispatch({
      type: "activeCitation",
      payload: { ...citation, i: i },
    });
    dispatch({
      type: "MODAL_TOGGLE",
      payload: true,
    });
  };

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages.length]);

  // useEffect(() => {
  //   const checkStatus = async () => {
  //     const userId = localStorage.getItem("userId");
  //     try {
  //       const result = await checkAdminStatus(userId);
  //       const isAdmin = result?.data?.is_admin === true;
  //       setIsAdmin(isAdmin);
  //     } catch (error) {
  //       console.error("Failed to check admin status:", error);
  //     }
  //   };
  //   checkStatus();
  // }, []);

  useEffect(() => {
    const arr = appData?.dataDrivenChat;
    if (conversationId && arr?.length) {
      const selectedConvo = arr.find(
        (item) => item?.session_id === conversationId
      );
      if (selectedConvo) {
        // Only trust position-based matching when this fires as a refresh of
        // the SAME conversation already on screen (e.g. a background getConvo()
        // landing while still viewing it) — capture that BEFORE updating
        // selectedConversation below. Cross-conversation position matching is
        // meaningless: it was carrying modelResponses over by array index alone
        // when switching to a totally different conversation, injecting one
        // conversation's multi-model answer onto an unrelated conversation's
        // message that happened to sit at the same index.
        const isSameConversationRefresh = selectedConversation?.session_id === conversationId;
        setSelectedConversation(selectedConvo);
        // The backend only knows the old single-model shape, so any refetch of
        // this conversation (e.g. the sidebar refresh after sending a message)
        // returns flat messages with no modelResponses. Carry over the
        // client-side multi-model data (by msg_id) so the dropdown/QA comparison
        // stays available instead of getting wiped by that resync.
        setMessages((prevMessages) => {
          const freshHistory = selectedConvo?.chat_history || [];
          const prevByMsgId = new Map(
            prevMessages
              .filter((m) => m?.msg_id_log?.msg_id)
              .map((m) => [m.msg_id_log.msg_id, m])
          );
          const merged = freshHistory.map((m, idx) => {
            // Fresh backend data (m) now comes with modelResponses/activeModel
            // already attached when the backend has them (persisted in Cosmos —
            // see retrive_chat_history), which is what lets the dropdown survive
            // logging in on a different device/browser. Local in-session state
            // (byId/byPosition) still wins when present, since it may hold more
            // up-to-date info this exact refresh doesn't (e.g. a model that's
            // still "pending", or the user's manually-chosen activeModel) — it's
            // merged over the backend copy rather than replacing it outright, so
            // a model missing locally still gets backfilled from Cosmos. Only
            // once neither backend nor local session state has anything do we
            // fall back to the localStorage cache.
            const msgId = m?.msg_id_log?.msg_id;
            const byId = prevByMsgId.get(msgId);
            const byPosition = isSameConversationRefresh ? prevMessages[idx] : null;
            const localSource = byId?.modelResponses
              ? byId
              : byPosition?.modelResponses
              ? byPosition
              : null;

            if (localSource) {
              return {
                ...m,
                modelResponses: { ...m.modelResponses, ...localSource.modelResponses },
                activeModel: localSource.activeModel || m.activeModel,
              };
            }
            if (m.modelResponses) {
              return m;
            }
            const stored = msgId ? loadModelResponse(msgId) : null;
            return stored?.modelResponses
              ? { ...m, modelResponses: stored.modelResponses, activeModel: stored.activeModel }
              : m;
          });

          // Same backend read-after-write lag concern as the modelResponses
          // backfill above: a message that was JUST added (and is already
          // showing locally) might not be in this fresh fetch yet, since the
          // map above only ever iterates over freshHistory. Only union when
          // refreshing the SAME conversation already on screen — carrying
          // messages across to a genuinely different conversation would be
          // exactly the cross-conversation bug isSameConversationRefresh
          // exists to prevent.
          if (isSameConversationRefresh) {
            // Only bot messages ever carry an id server-side (msg_id_log.msg_id
            // — see log_model_response in log_data_cosmos.py, which never
            // stamps one onto the user_msg doc it writes). A user message's
            // local id is the bare message_id shared with its paired bot
            // placeholder (both set from the same uuidv4() in handleSubmit),
            // so it can never appear in freshIds itself — checking it against
            // freshIds directly would make every already-synced user message
            // look "missing" forever and duplicate on every refresh. Instead:
            // find which bot turns haven't synced yet, then keep a user
            // message only if it's paired with one of those still-missing
            // bot turns.
            const freshBotIds = new Set(
              freshHistory.map((m) => m?.msg_id_log?.msg_id).filter(Boolean)
            );
            const missingBotIds = new Set(
              prevMessages
                .filter((m) => m?.msg_id_log?.msg_id && !freshBotIds.has(m.msg_id_log.msg_id))
                .map((m) => m.msg_id_log.msg_id)
            );
            const missingFromFresh = prevMessages.filter((m) => {
              const botId = m?.msg_id_log?.msg_id;
              if (botId) return missingBotIds.has(botId);
              return m?.message_id && missingBotIds.has(m.message_id);
            });
            return [...merged, ...missingFromFresh];
          }
          return merged;
        });
      } else if (selectedConversation?.session_id === conversationId) {
        // We already believe we're viewing exactly this conversation locally
        // (e.g. it was just created and seeded client-side) — the backend
        // simply hasn't caught up yet (no model has successfully logged a
        // response for it yet, so it doesn't exist server-side to be fetched).
        // That is NOT the same thing as an invalid/deleted conversation; don't
        // wipe local state just because this particular refresh doesn't have it.
      } else {
        // Genuinely invalid/deleted conversation_id — reset to new conversation.
        setSelectedConversation(undefined);
        setMessages([]);
        const params = new URLSearchParams(window.location.search);
        params.delete("conversation_id");
        window.history.replaceState({}, "", `${window.location.pathname}`);
      }
    } else {
      setMessages([]);
    }
    setPageLoader(false);
  }, [conversationId, appData?.dataDrivenChat]);

  // Phase-3 multi-model: lets the dropdown in BotMessage switch which model's
  // response is currently displayed for a given bot turn. This is a viewing
  // preference only (not persisted server-side), so it just updates local state.
  const handleSetActiveModel = (targetMsg, modelValue) => {
    setMessages((prevMessages) =>
      prevMessages.map((m) => (m === targetMsg ? { ...m, activeModel: modelValue } : m))
    );
  };

  const refreshLikeState = (messageId, newLikeState) => {
    if (!selectedConversation?.session_id) return;

    // Update in the parent messages state (for immediate UI response)
    setMessages((prevMessages) =>
      prevMessages.map((msg) => {
        if (msg?.msg_id_log?.msg_id === messageId) {
          return { ...msg, like_state: newLikeState };
        }
        return msg;
      })
    );

    // Update the conversation in appData (context)
    dispatch({
      type: "UPDATE_MESSAGE_LIKE_STATE",
      payload: {
        sessionId: selectedConversation.session_id,
        messageId: messageId,
        likeState: newLikeState,
      },
    });
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      scrollToBottom();
    }, 50); // delay slightly

    return () => clearTimeout(timeout);
  }, [messages.length]);

  return (
    <div
      className={`ai-converser-page flex items-center justify-between h-full gap-4 w-full bg-[#F6F8FB] p-3`}
    >
      <div
        className={`h-full m-auto bg-[#F6F8FB]  rounded-[10px] flex flex-col ${
          messages?.length === 0 ? "md:w-[80%] w-full" : "w-full"
        }`}
      >
        {appData.dataPageLoader || pageLoader ? (
          <div className="ilustration-container bg-[#F6F8FB] w-full flex justify-center items-center flex-1 min-h-0 mt-4">
            <Loader />
          </div>
        ) : (
          <>
            {messages?.length > 0 ? (
              <>
                <div
                  className="messages-container overflow-y-auto flex-1 min-h-0  bg-[#F6F8FB] "
                  id="chats-container"
                >
                  <div className="h-full flex flex-col gap-3 overflow-y-auto px-4">
                    {messages?.map((msg, i) => {
                      return (
                        <div
                          key={i}
                          className={`flex w-full gap-1 md:gap-2 ${
                            msg?.from === "User"
                              ? "flex-row-reverse items-start"
                              : "flex-row items-end"
                          }`}
                        >
                          <img
                            className={`h-4 w-4 md:h-7 md:w-7 ${
                              msg.from !== "User" && "mb-[40px]"
                            }`}
                            src={msg.from === "User" ? user : bot}
                            alt=""
                          />
                          {msg.from === "User" ? (
                            <UserMessage message={msg?.message} />
                          ) : (
                            <BotMessage
                              msg={msg}
                              messages={selectedConversation}
                              handleOpenCitation={handleOpenCitation}
                              isMultiChatRunning={appData?.isMultiChatRunning}
                              refreshLikeState={refreshLikeState}
                              onSetActiveModel={handleSetActiveModel}
                              scrollToBottom={scrollToBottom}
                              isLastMessage={i === messages.length - 1}
                            />
                          )}
                        </div>
                      );
                    })}
                    <div ref={chatEndRef} />
                    {loading && (
                      <div className="flex w-full gap-2 items-end">
                        <img
                          className="h-4 w-4 md:h-7 md:w-7"
                          src={bot}
                          alt=""
                        />
                        <div className="msg bot-msg max-w-[60%] flex-end bg-white border-[#D0F2FF] border-[1px] p-[0.3rem] rounded-[4px] relative w-fit">
                          <TypingIndicator />
                        </div>
                      </div>
                    )}
                    {/* <AlwaysScrollToBottom
                      containerRef={chatsContainerRef}
                      messages={messages}
                    /> */}
                  </div>
                </div>
              </>
            ) : (
              // <IllustrationContainer />
              <div className="ilustration-container bg-[#F6F8FB] w-full flex flex-col justify-center items-center flex-1 min-h-0 overflow-y-auto px-4 py-8 gap-6">
                <img src={NecaLogo} alt="NECA" className="h-14 w-auto" />

                <div className="text-center -mt-2">
                  <h1 className="text-[28px] md:text-[32px] font-semibold text-[#242424] mb-2">
                    {greeting}
                    {firstName ? `, ${firstName}` : ""}
                  </h1>
                  <p className="text-[#616161] text-base max-w-[560px]">
                    I'm <span className="text-[#00AEEF] font-medium">Niki</span>, your AI
                    assistant for electrical work and regulations in Australia.
                  </p>
                </div>

                <div className="max-w-[640px] w-full">
                  <p className="text-[11px] font-semibold tracking-wide uppercase text-[#9A9A9A] mb-3 text-center md:text-left">
                    Getting the best from Niki
                  </p>
                  <ul className="flex flex-col gap-3">
                    {TIPS.map((tip) => (
                      <li key={tip.title} className="flex items-start gap-3">
                        <span className="mt-[9px] h-[6px] w-[6px] rounded-full bg-[#00AEEF] shrink-0" />
                        <span className="text-[#414141] text-[15px] leading-6">
                          <span className="font-semibold">{tip.title}</span> {tip.body}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </>
        )}
        <InputFooter
          setMessages={setMessages}
          conversationId={conversationId}
          loading={loading}
          setLoading={setLoading}
          messages={messages}
        />
      </div>
    </div>
  );
};

export default DataDrivenChat;
